import { notifyGamificationRewardOutcomes } from "@app/gamification/show-reward-outcome";
import type { RatingSummary, RatingSummaryResponse } from "@core/ratings/types";
import { parseError, URLS } from "@core/shared/utils";
import { errorToast, successToast } from "@design-system/components/toast";
import { useIsLoggedIn } from "@framework/account/hooks";
import { useAction } from "@framework/shared/hooks";
import { trans } from "@mongez/localization";
import type { FormSubmitOptions } from "@mongez/react-form";
import { useCallback, useEffect, useState } from "react";

export function useLessonRating(lessonId: number) {
  const [rating, setRating] = useState<RatingSummary>();
  const [loading, setLoading] = useState(true);
  const updateRating = useAction<RatingSummaryResponse>(URLS.ratings.update);
  const isLoggedIn = useIsLoggedIn();

  const loadRating = useCallback(async () => {
    setLoading(true);

    try {
      const response = await fetch(
        `${URLS.ratings.root}?type=lesson&typeId=${lessonId}`,
      );
      const data = (await response.json()) as RatingSummaryResponse;
      setRating(data.rating);
    } catch {
      errorToast(trans("ratings.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [lessonId]);

  useEffect(() => {
    loadRating();
  }, [loadRating]);

  const submitRating = async ({ values, form }: FormSubmitOptions) => {
    const response = await updateRating({
      typeId: lessonId,
      value: Number(values.value),
    });

    form.submitting(false);

    if (response.error) {
      errorToast(parseError(response) || trans("ratings.saveFailed"));
      return;
    }

    setRating(response.data.rating);
    notifyGamificationRewardOutcomes(response.data.rewardOutcome);
    successToast(trans("ratings.saved"));
  };

  return {
    canRate: Boolean(rating?.canRate),
    isLoggedIn,
    loading,
    rating,
    submitRating,
  };
}
